import { Autocomplete, CircularProgress, Grid2 as Grid, Stack, Typography } from '@mui/material'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { DesktopDatePicker } from '@mui/x-date-pickers/DesktopDatePicker'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import Paper from 'components/@extended/Paper'
import { TextField } from 'components/@extended/TextField'
import SingleOrganizationSelector from 'components/SingleOrganizationSelector'
import { useCreatePlanContext } from 'contexts/CreatePlanContextProvider'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'

dayjs.extend(utc)
import useAuth from 'hooks/useAuth'
import { useFetchUser, useSearchUsers } from 'hooks/useUsers'
import type { FC } from 'react'
import React, { useEffect, useMemo, useState } from 'react'
import type { CompensationPlan } from 'types/comp-plan'
import { findPeriodById, DATE_FORMAT } from 'types/periods'
import type { UserRole } from 'types/role'
import type { User } from 'types/user'
import type { UserProfile } from 'types/user-profile'
import { getMemberRole } from 'types/user-profile'

const EmployeeDetailForm: FC = () => {
  // Context and Auth
  const { token } = useAuth()
  const { compensationTemplate, plan, setPlan, mode, setEmployeeName, originalEffectiveDate, withUserId } =
    useCreatePlanContext()

  // State
  const [profile, setProfile] = useState<UserProfile>({
    roles: [] as UserRole[],
    user: { firstName: '', lastName: '', position: '' } as User,
  } as UserProfile)
  const [isDataLoaded, setIsDataLoaded] = useState(false)
  const [selectedOrganization, setSelectedOrganization] = useState<string>('')
  const [effectiveDate, setEffectiveDateState] = useState<dayjs.Dayjs | null>(null)
  const [selectedUser, setSelectedUser] = useState<User | null>(null)
  const [hasInitializedEffectiveDate, setHasInitializedEffectiveDate] = useState(false)

  // Wrapper to log all setEffectiveDate calls
  const setEffectiveDate = (date: dayjs.Dayjs | null) => {
    console.log('setEffectiveDate called with:', date?.format('YYYY-MM-DD'), 'from:', new Error().stack)
    setEffectiveDateState(date)
  }

  // Fetching Users
  const { users, isLoading: userSearchIsLoading } = useSearchUsers(
    token!,
    {
      organizationIds: compensationTemplate?.template.organizationIds,
      perPage: 1000,
    },
    compensationTemplate != null && compensationTemplate.template.organizationIds != null
  )

  const userId = withUserId || (plan && plan.userId ? plan.userId : null)
  const { userProfile, isLoading: userIsLoading } = useFetchUser(
    token!,
    userId!,
    [['explicitRolesOnly', 'true']],
    !!userId
  )

  // Extract the period end date from the compensation template
  const maxEffectiveDate = useMemo(() => {
    if (compensationTemplate?.period && compensationTemplate.template.periodId) {
      const matchingPeriod = findPeriodById([compensationTemplate.period], compensationTemplate.template.periodId)
      return matchingPeriod?.endDate ? dayjs.utc(matchingPeriod.endDate) : undefined
    }
    return undefined
  }, [compensationTemplate])

  // Extract the period start date from the compensation template
  const templatePeriodStartDate = useMemo(() => {
    console.log('Computing templatePeriodStartDate:', {
      compensationTemplate: compensationTemplate,
      period: compensationTemplate?.period,
      templatePeriodId: compensationTemplate?.template.periodId,
    })
    if (compensationTemplate?.period && compensationTemplate.template.periodId) {
      const matchingPeriod = findPeriodById([compensationTemplate.period], compensationTemplate.template.periodId)
      console.log('Found matching period:', matchingPeriod)
      return matchingPeriod?.startDate ? dayjs.utc(matchingPeriod.startDate) : undefined
    }
    return undefined
  }, [compensationTemplate])

  // Effects
  useEffect(() => {
    if (!userIsLoading && !userSearchIsLoading) {
      setIsDataLoaded(true)
    }
  }, [userIsLoading, userSearchIsLoading, userProfile])

  useEffect(() => {
    console.log('Effective date useEffect:', {
      hasInitializedEffectiveDate,
      templatePeriodStartDate: templatePeriodStartDate?.format('YYYY-MM-DD'),
      planEffectiveDate: plan.effectiveDate,
      planId: plan.id,
      condition: !hasInitializedEffectiveDate && templatePeriodStartDate !== undefined,
    })
    if (!hasInitializedEffectiveDate && templatePeriodStartDate !== undefined) {
      // For new plans (no plan.id), always use template period start date
      // For existing plans, use the existing effective date
      if (plan.id && plan.effectiveDate) {
        console.log('Setting from existing plan.effectiveDate (existing plan):', plan.effectiveDate)
        setEffectiveDate(dayjs.utc(plan.effectiveDate))
      } else {
        console.log('Setting to template period start date (new plan):', templatePeriodStartDate.format('YYYY-MM-DD'))
        // Always default to template period start date for new plans
        setEffectiveDate(templatePeriodStartDate)
      }
      setHasInitializedEffectiveDate(true)
    }
  }, [plan.effectiveDate, hasInitializedEffectiveDate, templatePeriodStartDate])

  useEffect(() => {
    const tempPlan = { ...plan }
    // tempPlan.organizationId = ''
    if (effectiveDate) {
      // Format the date as YYYY-MM-DD and then parse it as UTC to ensure it's midnight UTC
      const dateString = effectiveDate.format('YYYY-MM-DD')
      tempPlan.effectiveDate = dayjs.utc(dateString).format('YYYY-MM-DDTHH:mm:ss.SSSZ')
    } else {
      tempPlan.effectiveDate = ''
    }
    setPlan(tempPlan)
  }, [effectiveDate])

  useEffect(() => {
    if (userProfile && userProfile.roles && userProfile.roles.length > 0) {
      const memberRole = getMemberRole(userProfile)
      if (memberRole) {
        setOrganizationHandler(memberRole.organization.id)
      }
    }
  }, [userProfile])

  useEffect(() => {
    if (isDataLoaded && userProfile?.user) {
      setEmployeeName(`${userProfile.user.firstName} ${userProfile.user.lastName}`)
      if (!plan.description) {
        setPlan((prevPlan: CompensationPlan) => ({ ...prevPlan, position: '' }))
      }
      if (!plan.userId) {
        setPlan((prevPlan: CompensationPlan) => ({
          ...prevPlan,
          userId: userProfile.user.id,
          position: userProfile.user.position,
        }))
      }
      setProfile(userProfile)
    }
  }, [isDataLoaded, userProfile])

  // Handlers
  const setOrganizationHandler = (value: string) => {
    setSelectedOrganization(value)
    const tempPlan = { ...plan }
    tempPlan.organizationId = value
    setPlan(tempPlan)
  }

  const onChangeEffectiveDate = (newValue: dayjs.Dayjs | null) => {
    if (newValue) {
      // Parse the date as UTC by extracting the date string first
      const dateString = newValue.format('YYYY-MM-DD')
      const utcDate = dayjs.utc(dateString)

      const userStartDate = profile.user?.startDate ? dayjs.utc(profile.user.startDate) : null

      if (userStartDate && utcDate.isBefore(userStartDate)) {
        setEffectiveDate(userStartDate)
        const tempPlan = { ...plan }
        const userStartDateString = userStartDate.format('YYYY-MM-DD')
        tempPlan.effectiveDate = dayjs.utc(userStartDateString).format('YYYY-MM-DDTHH:mm:ss.SSSZ')
        setPlan(tempPlan)
        return
      }

      setEffectiveDate(utcDate)
      const tempPlan = { ...plan }
      tempPlan.effectiveDate = utcDate.format('YYYY-MM-DDTHH:mm:ss.SSSZ')
      setPlan(tempPlan)
    }
  }

  const onChangeSelectedEmployee = (event: any, newValue: User | null) => {
    if (compensationTemplate) {
      setSelectedUser(newValue)

      const tempPlan = { ...plan }
      tempPlan.userId = newValue?.id || ''
      tempPlan.description = newValue?.position || ''
      setEmployeeName(newValue ? `${newValue.firstName} ${newValue.lastName}` : '')
      setPlan(tempPlan)
    }
  }

  const onChangePosition = (event: React.ChangeEvent<HTMLInputElement>) => {
    const tempPlan = { ...plan }
    tempPlan.description = event.target.value
    setPlan(tempPlan)
  }

  // Loading State
  if (userSearchIsLoading || userIsLoading || !isDataLoaded) {
    return (
      <Grid container spacing={12}>
        <Grid
          size={{ xs: 12 }}
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '100%',
          }}
        >
          <CircularProgress color="primary" />
          Loading
        </Grid>
      </Grid>
    )
  }

  console.log('maxEffectiveDate', maxEffectiveDate)

  // JSX Return
  return (
    <Paper sx={{ p: 3 }}>
      <Grid container size={{ xs: 12 }} spacing={4}>
        <Grid size={{ xs: 12 }}>
          <Typography variant="h4">Employee Details</Typography>
        </Grid>
        <Grid size={{ xs: 12 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid size={{ xs: 12, lg: 4 }}>
              <TextField
                fullWidth
                label="Template Name *"
                InputProps={{
                  readOnly: true,
                }}
                value={compensationTemplate?.template.label}
                variant="outlined"
                placeholder="Template Name"
                disabled={!!plan.id}
                shrink
                pill
              />
            </Grid>
            <Grid size={{ xs: 12, lg: 4 }}>
              <TextField
                fullWidth
                label="Intended Role"
                InputProps={{
                  readOnly: true,
                }}
                value={compensationTemplate?.template.position}
                variant="outlined"
                disabled={!!plan.id}
                shrink
                pill
              />
            </Grid>
            <Grid size={{ xs: 12, lg: 4 }}>
              <TextField
                label="Name / Description"
                fullWidth
                type="text"
                placeholder="Name / Description"
                value={plan.description || ''}
                onChange={onChangePosition}
                InputProps={{
                  readOnly: mode === 'REVIEW',
                }}
                variant="outlined"
                shrink
                pill
              />
            </Grid>
          </Grid>
        </Grid>
        <Grid container size={{ xs: 12 }} spacing={2} alignItems="flex-start">
          <Grid size={{ xs: 12, lg: 4 }}>
            {userProfile?.user && (plan.id || withUserId) ? (
              <TextField
                key="employee-select"
                fullWidth
                label="Employee Name *"
                InputProps={{
                  readOnly: true,
                }}
                value={
                  profile.user &&
                  profile.user.firstName &&
                  profile.user.lastName &&
                  profile.user.firstName !== '' &&
                  profile.user.lastName !== ''
                    ? `${profile.user.firstName} ${profile.user.lastName}`
                    : null
                }
                variant="outlined"
                shrink
                pill
              />
            ) : (
              <Autocomplete
                id="employee-select"
                options={users}
                fullWidth
                autoHighlight
                getOptionLabel={(option) => (option ? `${option.firstName} ${option.lastName}` : '')}
                onChange={onChangeSelectedEmployee}
                value={selectedUser}
                isOptionEqualToValue={(option, value) => option?.id === value?.id}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Employee Name"
                    placeholder="Select Employee *"
                    inputProps={{
                      ...params.inputProps,
                      autoComplete: 'new-password',
                    }}
                    variant="outlined"
                    shrink
                    pill
                  />
                )}
              />
            )}
          </Grid>
          <Grid size={{ xs: 12, lg: 4 }}>
            <SingleOrganizationSelector
              key="employee-organization-select"
              userProfile={profile}
              selectedOrganizationId={selectedOrganization}
              handleChange={(value) => setOrganizationHandler(value)}
              style={{ marginTop: 12, paddingBottom: 0 }}
              memberRolesOnly
              variant="outlined"
              disabled={
                !plan.userId ||
                (selectedOrganization !== '' &&
                  userProfile &&
                  userProfile.roles.filter((role) => role.role.name === 'Member').length === 1)
              }
              space={1}
            />
          </Grid>
          <Grid size={{ xs: 12, lg: 4 }}>
            <Stack spacing={1} sx={{ flexGrow: 1 }}>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DesktopDatePicker
                  label="Effective Date *"
                  value={effectiveDate}
                  onChange={onChangeEffectiveDate}
                  format={DATE_FORMAT}
                  disabled={mode === 'REVIEW'}
                  minDate={profile.user?.startDate ? dayjs(profile.user.startDate) : undefined}
                  maxDate={maxEffectiveDate}
                  slotProps={{
                    textField: {
                      variant: 'outlined',
                      InputProps: {
                        readOnly: true,
                      },
                      InputLabelProps: {
                        shrink: true,
                        sx: {
                          '&.Mui-focused': {
                            color: (theme) => theme.palette.primary.main,
                          },
                        },
                      },
                      sx: {
                        '& .MuiOutlinedInput-root': {
                          borderRadius: (theme) => theme.shape.borderRadius * 5.25,
                          '& fieldset': {
                            borderColor: (theme) => theme.palette.primary.main,
                          },
                          '&:hover fieldset': {
                            borderColor: (theme) => theme.palette.primary.dark,
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: (theme) => theme.palette.secondary.main,
                          },
                        },
                      },
                      helperText: originalEffectiveDate
                        ? `Original effective date: ${dayjs.utc(originalEffectiveDate).format(DATE_FORMAT)}`
                        : profile.user?.startDate
                          ? `Employee start date: ${dayjs.utc(profile.user.startDate).format(DATE_FORMAT)}`
                          : 'Click calendar icon to select date',
                    },
                  }}
                />
              </LocalizationProvider>
            </Stack>
          </Grid>
        </Grid>
      </Grid>
    </Paper>
  )
}

export default EmployeeDetailForm
